import {
  TABLE_DEFAULT_COLS,
  TABLE_DEFAULT_ROWS,
  TABLE_MAX_COLS,
  TABLE_MAX_ROWS,
  createTableDoc,
  sanitizeTableDoc,
} from "@/cms/blocks/table";
import {
  AVERAGE_CHARACTER_WIDTH,
  CELL_HORIZONTAL_PADDING,
  DEFAULT_COLUMN_WIDTH,
  DEFAULT_ROW_HEIGHT,
  FORMULA_ERROR,
  MAX_SMART_COLUMN_WIDTH,
  MIN_COLUMN_WIDTH,
  type CellAlign,
  type CellType,
  type SelectionState,
  type TableCellModel,
} from "./types";

export const stringify = (v: unknown) => JSON.stringify(v);

export const safeSanitize = (doc: any) =>
  sanitizeTableDoc(doc ?? createTableDoc(), {
    maxRows: TABLE_MAX_ROWS,
    maxCols: TABLE_MAX_COLS,
  });

export const normalizeFixedIndex = (value: unknown, size: number) =>
  typeof value === "number" &&
  Number.isInteger(value) &&
  value >= 0 &&
  value < size
    ? value
    : undefined;

export function createId(row: number, col: number) {
  const randomPart =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `${row}-${col}-${randomPart}`;
}

export function createCell(
  row: number,
  col: number,
  type: CellType = "cell",
  text = "",
): TableCellModel {
  return {
    id: createId(row, col),
    text,
    type,
    rowspan: 1,
    colspan: 1,
    hidden: false,
    bold: false,
    italic: false,
    align: "left",
    direction: "ltr",
  };
}

export function cloneTable(table: TableCellModel[][]) {
  return table.map((row) => row.map((cell) => ({ ...cell })));
}

export function clampSize(value: unknown, max: number) {
  const parsed =
    typeof value === "number" ? value : Number.parseInt(String(value), 10);
  if (Number.isNaN(parsed)) return 1;
  return Math.min(Math.max(Math.round(parsed), 1), max);
}

export function getColumnCount(table: TableCellModel[][]) {
  return Math.max(1, ...table.map((row) => row.length));
}

export function createColumnWidths(
  columnCount: number,
  width = DEFAULT_COLUMN_WIDTH,
) {
  return Array.from({ length: Math.max(columnCount, 1) }, () => width);
}

export function resizeColumnWidths(
  widths: number[],
  columnIndex: number,
  nextWidth: number,
) {
  return widths.map((width, index) =>
    index === columnIndex
      ? Math.max(MIN_COLUMN_WIDTH, Math.round(nextWidth))
      : width,
  );
}

export function areNumberArraysEqual(a: number[], b: number[]) {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function estimateTextWidth(text: string) {
  const lines = String(text || "").split("\n");
  const longestLineLength = Math.max(...lines.map((line) => line.length), 0);
  return longestLineLength * AVERAGE_CHARACTER_WIDTH + CELL_HORIZONTAL_PADDING;
}

export function columnIndexToName(index: number) {
  let name = "";
  let current = index + 1;

  while (current > 0) {
    const remainder = (current - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    current = Math.floor((current - 1) / 26);
  }

  return name;
}

function columnNameToIndex(name: string) {
  return (
    name
      .toUpperCase()
      .split("")
      .reduce((sum, char) => sum * 26 + char.charCodeAt(0) - 64, 0) - 1
  );
}

export function getCellLabel(rowIndex: number, colIndex: number) {
  return `${columnIndexToName(colIndex)}${rowIndex + 1}`;
}

function parseCellRef(ref: string) {
  const match = String(ref).trim().match(/^([A-Z]+)([0-9]+)$/i);
  if (!match) return null;
  return {
    col: columnNameToIndex(match[1]),
    row: Number(match[2]) - 1,
  };
}

function formatFormulaValue(value: number) {
  if (!Number.isFinite(value)) return FORMULA_ERROR;
  return Number.isInteger(value)
    ? String(value)
    : String(Number(value.toFixed(6)));
}

function getCellRawValue(table: TableCellModel[][], row: number, col: number) {
  return table[row]?.[col]?.text ?? "";
}

function getRangeValues(
  table: TableCellModel[][],
  startRef: string,
  endRef: string,
  visited = new Set<string>(),
) {
  const start = parseCellRef(startRef);
  const end = parseCellRef(endRef);
  if (!start || !end) return [];

  const fromRow = Math.min(start.row, end.row);
  const toRow = Math.max(start.row, end.row);
  const fromCol = Math.min(start.col, end.col);
  const toCol = Math.max(start.col, end.col);
  const values: number[] = [];

  for (let row = fromRow; row <= toRow; row += 1) {
    for (let col = fromCol; col <= toCol; col += 1) {
      values.push(getCellNumericValue(table, row, col, visited));
    }
  }

  return values;
}

function evaluateAggregate(
  table: TableCellModel[][],
  name: string,
  args: string[],
  visited = new Set<string>(),
) {
  const values = args
    .flatMap((arg) => {
      const cleanArg = arg.trim();
      const rangeMatch = cleanArg.match(/^([A-Z]+[0-9]+)\s*:\s*([A-Z]+[0-9]+)$/i);
      const cellMatch = cleanArg.match(/^[A-Z]+[0-9]+$/i);

      if (rangeMatch) return getRangeValues(table, rangeMatch[1], rangeMatch[2], visited);
      if (cellMatch) {
        const ref = parseCellRef(cleanArg);
        return ref ? [getCellNumericValue(table, ref.row, ref.col, visited)] : [];
      }

      const number = Number(cleanArg.replaceAll(",", ""));
      return Number.isFinite(number) ? [number] : [];
    })
    .filter((value) => Number.isFinite(value));

  const normalizedName = name.toUpperCase();
  if (normalizedName === "COUNT") return values.length;
  if (!values.length) return 0;
  if (normalizedName === "SUM") return values.reduce((sum, value) => sum + value, 0);
  if (normalizedName === "AVG") return values.reduce((sum, value) => sum + value, 0) / values.length;
  if (normalizedName === "MIN") return Math.min(...values);
  if (normalizedName === "MAX") return Math.max(...values);
  return Number.NaN;
}

function replaceAggregates(
  table: TableCellModel[][],
  expression: string,
  visited = new Set<string>(),
) {
  return expression.replace(
    /\b(SUM|AVG|MIN|MAX|COUNT)\s*\(([^()]*)\)/gi,
    (_, fnName: string, argsText: string) => {
      const args = argsText.split(",").map((arg) => arg.trim()).filter(Boolean);
      return String(evaluateAggregate(table, fnName, args, visited));
    },
  );
}

function replaceCellRefs(
  table: TableCellModel[][],
  expression: string,
  visited = new Set<string>(),
) {
  return expression
    .replace(/\b([A-Z]+[0-9]+)\s*:\s*([A-Z]+[0-9]+)\b/gi, "0")
    .replace(/\b([A-Z]+[0-9]+)\b/gi, (refText) => {
      const ref = parseCellRef(refText);
      return ref ? String(getCellNumericValue(table, ref.row, ref.col, visited)) : "0";
    });
}

function isSafeMathExpression(expression: string) {
  return expression
    .split("")
    .every((char) => "0123456789+-*/(). ".includes(char));
}

function evaluateFormula(
  table: TableCellModel[][],
  raw: string,
  visited = new Set<string>(),
) {
  try {
    let expression = String(raw).trim();
    if (!expression.startsWith("=")) return { value: expression, error: null };

    expression = expression.slice(1).trim();
    expression = replaceAggregates(table, expression, visited);
    expression = replaceCellRefs(table, expression, visited);

    if (!isSafeMathExpression(expression)) {
      return { value: FORMULA_ERROR, error: "Invalid formula" };
    }

    const value = Function(`"use strict"; return (${expression});`)();
    return { value: formatFormulaValue(Number(value)), error: null };
  } catch {
    return { value: FORMULA_ERROR, error: "Invalid formula" };
  }
}

function getCellNumericValue(
  table: TableCellModel[][],
  row: number,
  col: number,
  visited = new Set<string>(),
) {
  const key = `${row}:${col}`;
  if (visited.has(key)) return 0;

  const text = String(getCellRawValue(table, row, col)).trim();
  if (text.startsWith("=")) {
    const result = evaluateFormula(table, text, new Set([...visited, key]));
    const number = Number(result.value);
    return Number.isFinite(number) ? number : 0;
  }

  const number = Number(text.replaceAll(",", ""));
  return Number.isFinite(number) ? number : 0;
}

export function getCellDisplayValue(
  table: TableCellModel[][],
  cell: TableCellModel,
) {
  const raw = String(cell.text ?? "");
  return raw.trim().startsWith("=") ? evaluateFormula(table, raw).value : raw;
}

export function getSmartColumnWidths(table: TableCellModel[][]) {
  const columnCount = getColumnCount(table);
  return Array.from({ length: columnCount }, (_, colIndex) => {
    const visibleCells = table
      .map((row) => row[colIndex])
      .filter((cell): cell is TableCellModel => Boolean(cell && !cell.hidden));
    const widestContent = Math.max(
      MIN_COLUMN_WIDTH,
      ...visibleCells.map((cell) =>
        estimateTextWidth(getCellDisplayValue(table, cell)),
      ),
    );
    return Math.min(
      Math.max(Math.round(widestContent), MIN_COLUMN_WIDTH),
      MAX_SMART_COLUMN_WIDTH,
    );
  });
}

export function autoSizeTextarea(textarea: HTMLTextAreaElement | null) {
  if (!textarea) return;
  textarea.style.height = "auto";
  textarea.style.height = `${textarea.scrollHeight}px`;
}

export function normalizeSelection(selection: SelectionState | null) {
  if (!selection?.start || !selection?.end) return null;
  return {
    fromRow: Math.min(selection.start.row, selection.end.row),
    toRow: Math.max(selection.start.row, selection.end.row),
    fromCol: Math.min(selection.start.col, selection.end.col),
    toCol: Math.max(selection.start.col, selection.end.col),
  };
}

export function isCellSelected(
  row: number,
  col: number,
  selection: SelectionState,
) {
  const range = normalizeSelection(selection);
  if (!range) return false;
  return (
    row >= range.fromRow &&
    row <= range.toRow &&
    col >= range.fromCol &&
    col <= range.toCol
  );
}

export function getSelectedAnchor(selection: SelectionState) {
  const range = normalizeSelection(selection);
  if (!range) return { row: 0, col: 0 };
  return { row: range.fromRow, col: range.fromCol };
}

function findFirstTable(node: any): any | null {
  if (!node) return null;
  if (node.type === "table") return node;
  if (Array.isArray(node.content)) {
    for (const child of node.content) {
      const found = findFirstTable(child);
      if (found) return found;
    }
  }
  return null;
}

function extractCellText(cell: any) {
  const parts: string[] = [];
  const walk = (node: any) => {
    if (!node) return;
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (typeof node.text === "string") parts.push(node.text);
    if (Array.isArray(node.content)) node.content.forEach(walk);
  };
  walk(cell?.content);
  return parts.join("\n").trim();
}

export function tableFromDoc(doc: any): {
  table: TableCellModel[][];
  columnWidths: number[];
  clamped: boolean;
} {
  const { doc: safeDoc, clamped } = safeSanitize(doc ?? createTableDoc());
  const tableNode = findFirstTable(safeDoc);
  const rows = Array.isArray(tableNode?.content) ? tableNode.content : [];
  const grid: TableCellModel[][] = [];
  const widths: number[] = [];

  rows.slice(0, TABLE_MAX_ROWS).forEach((rowNode: any, rowIndex: number) => {
    if (!grid[rowIndex]) grid[rowIndex] = [];
    const cells = Array.isArray(rowNode?.content) ? rowNode.content : [];
    let colIndex = 0;
    cells.forEach((cellNode: any) => {
      while (grid[rowIndex]?.[colIndex]) colIndex += 1;
      if (colIndex >= TABLE_MAX_COLS) return;
      const attrs = cellNode?.attrs ?? {};
      const rowspan = Math.min(
        clampSize(attrs.rowspan ?? 1, TABLE_MAX_ROWS),
        TABLE_MAX_ROWS - rowIndex,
      );
      const colspan = Math.min(
        clampSize(attrs.colspan ?? 1, TABLE_MAX_COLS),
        TABLE_MAX_COLS - colIndex,
      );
      const cell = createCell(
        rowIndex,
        colIndex,
        cellNode?.type === "tableHeader" ? "header" : "cell",
        typeof attrs.raw === "string" ? attrs.raw : extractCellText(cellNode),
      );
      cell.rowspan = rowspan;
      cell.colspan = colspan;
      cell.bold = attrs.bold === true;
      cell.italic = attrs.italic === true;
      cell.align =
        attrs.textAlign === "center" || attrs.textAlign === "right"
          ? (attrs.textAlign as CellAlign)
          : "left";
      cell.direction = attrs.direction === "rtl" ? "rtl" : "ltr";
      grid[rowIndex][colIndex] = cell;

      const colwidth = Array.isArray(attrs.colwidth) ? attrs.colwidth : [];
      for (let offset = 0; offset < colspan; offset += 1) {
        const width = colwidth[offset];
        if (typeof width === "number" && width > 0) {
          widths[colIndex + offset] = Math.max(MIN_COLUMN_WIDTH, width);
        }
      }

      for (let r = rowIndex; r < rowIndex + rowspan; r += 1) {
        if (!grid[r]) grid[r] = [];
        for (let c = colIndex; c < colIndex + colspan; c += 1) {
          if (r === rowIndex && c === colIndex) continue;
          grid[r][c] = {
            ...createCell(r, c),
            hidden: true,
          };
        }
      }
      colIndex += colspan;
    });
  });

  const rowCount = Math.max(1, Math.min(grid.length, TABLE_MAX_ROWS));
  const colCount = Math.max(
    1,
    Math.min(Math.max(...grid.map((row) => row.length), 1), TABLE_MAX_COLS),
  );
  const normalized = Array.from({ length: rowCount }, (_, rowIndex) =>
    Array.from({ length: colCount }, (_, colIndex) => {
      const existing = grid[rowIndex]?.[colIndex];
      return existing ?? createCell(rowIndex, colIndex);
    }),
  );
  return {
    table: normalized,
    columnWidths: Array.from(
      { length: colCount },
      (_, index) => widths[index] ?? DEFAULT_COLUMN_WIDTH,
    ),
    clamped,
  };
}

export function tableToDoc(table: TableCellModel[][], columnWidths: number[]) {
  return {
    type: "doc",
    content: [
      {
        type: "table",
        content: table.map((row) => ({
          type: "tableRow",
          content: row
            .map((cell, colIndex) => ({ cell, colIndex }))
            .filter(({ cell }) => !cell.hidden)
            .map(({ cell, colIndex }) => {
              const computed = getCellDisplayValue(table, cell);
              return {
                type: cell.type === "header" ? "tableHeader" : "tableCell",
                attrs: {
                  colspan: cell.colspan,
                  rowspan: cell.rowspan,
                  colwidth: columnWidths.slice(
                    colIndex,
                    colIndex + cell.colspan,
                  ),
                  bold: cell.bold,
                  italic: cell.italic,
                  textAlign: cell.align,
                  direction: cell.direction,
                  raw: cell.text,
                  computed,
                },
                content: [
                  {
                    type: "paragraph",
                    content: computed
                      ? [{ type: "text", text: computed }]
                      : [],
                  },
                ],
              };
            }),
        })),
      },
    ],
  };
}

export function createTableModel(
  rows = TABLE_DEFAULT_ROWS,
  cols = TABLE_DEFAULT_COLS,
  headerRow = true,
  headerColumn = false,
) {
  const safeRows = clampSize(rows, TABLE_MAX_ROWS);
  const safeCols = clampSize(cols, TABLE_MAX_COLS);
  return Array.from({ length: safeRows }, (_, row) =>
    Array.from({ length: safeCols }, (_, col) => {
      const isHeader = (headerRow && row === 0) || (headerColumn && col === 0);
      return createCell(row, col, isHeader ? "header" : "cell");
    }),
  );
}

export { DEFAULT_ROW_HEIGHT };
